/**
 * Vendor Families Hook
 *
 * Hook for vendors to load and manage their available product families.
 * Filters families based on vendor's enabledFamilies from config.
 */

import { useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { VendorState } from '../../redux/vendor/rootReducer';
import { useProductFamiliesApi } from './use-product-families-api.hook';
import {
  setProductFamilies,
  setProductFamiliesLoading,
  setProductFamiliesError,
} from '../../redux/productFamilies/productFamiliesActions';
import { ProductFamily } from '../models/product-family.model';
import { config } from './use-settings-api.hook';

interface UseVendorFamiliesResult {
  /** List of families available to the vendor */
  families: ProductFamily[];
  /** Loading state */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Get the default family */
  defaultFamily: ProductFamily | null;
  /** Get a family by ID */
  getFamilyById: (id: string) => ProductFamily | undefined;
  /** Get a family by slug */
  getFamilyBySlug: (slug: string) => ProductFamily | undefined;
  /** Refresh families from API */
  refresh: () => Promise<void>;
}

export const useVendorFamilies = (): UseVendorFamiliesResult => {
  const dispatch = useDispatch();
  const productFamiliesApi = useProductFamiliesApi();

  const { families: allFamilies, loading, error } = useSelector(
    (state: VendorState) => state.productFamilies
  );

  // Always load fresh families on mount to pick up translation updates
  useEffect(() => {
    if (!loading) {
      loadFamilies();
    }
  }, []);

  const loadFamilies = async () => {
    dispatch(setProductFamiliesLoading(true));
    dispatch(setProductFamiliesError(null));

    try {
      console.log('[useVendorFamilies] Loading families...');
      const data = await productFamiliesApi.findAllFamilies({ activeOnly: true });
      console.log('[useVendorFamilies] Loaded families:', data);
      dispatch(setProductFamilies(data || []));
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to load product families';
      dispatch(setProductFamiliesError(errorMessage));
      console.error('[useVendorFamilies] Error loading product families:', err);
    } finally {
      dispatch(setProductFamiliesLoading(false));
    }
  };

  // Filter families based on vendor's enabledFamilies from config
  const families = useMemo(() => {
    const enabledFamilyEntries = config?.enabledFamilies;

    // If vendor has enabledFamilies set, filter by those
    if (enabledFamilyEntries && enabledFamilyEntries.length > 0) {
      // enabledFamilies can be plain string IDs or populated objects (with _id field)
      // depending on whether the backend populates the field
      const enabledIds = enabledFamilyEntries.map((entry: any) =>
        typeof entry === 'string' ? entry : entry?._id
      ).filter(Boolean);

      if (enabledIds.length > 0) {
        const filtered = allFamilies.filter(f => enabledIds.includes(f._id));
        console.log('[useVendorFamilies] Filtered families by enabledFamilies:', filtered.length, 'of', allFamilies.length);
        return filtered;
      }
    }

    // If no enabledFamilies set, return ALL active families
    // This allows vendors to see and use all available product families
    console.log('[useVendorFamilies] No enabledFamilies set, returning all families:', allFamilies.length);
    return allFamilies;
  }, [allFamilies, config?.enabledFamilies]);

  // Memoize these to prevent unnecessary re-renders in consuming components
  const defaultFamily = useMemo(
    () => families.find(f => f.isDefault) || families[0] || null,
    [families]
  );

  const getFamilyById = useCallback(
    (id: string) => families.find(f => f._id === id),
    [families]
  );

  const getFamilyBySlug = useCallback(
    (slug: string) => families.find(f => f.slug === slug),
    [families]
  );

  return {
    families,
    loading,
    error,
    defaultFamily,
    getFamilyById,
    getFamilyBySlug,
    refresh: loadFamilies,
  };
};

export default useVendorFamilies;
